import i18n from '../../i18n';
import { getRuntimeConfig } from '../runtimeConfig';

export const REQUEST_ID_HEADER = 'X-Request-Id';

const isJsonResponse = response => {
    const contentType = response?.headers.get('content-type') ?? '';

    return (
        contentType.includes('application/json') ||
        contentType.includes('application/problem+json')
    );
};

/**
 * True when the request was cancelled through its AbortSignal.
 * Callers use it to ignore cancellations instead of reporting an error.
 */
export const isAbortError = error =>
    error?.name === 'AbortError' || error?.code === 20; /* DOMException.ABORT_ERR */

const createAbortError = () => {
    try {
        return new DOMException('The operation was aborted.', 'AbortError');
    } catch {
        const error = new Error('The operation was aborted.');
        error.name = 'AbortError';
        return error;
    }
};

/**
 * Opaque request identifier for X-Request-Id (no user or session data).
 * The same value is sent on the original request and on its 401 retry.
 */
export const createRequestId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }

    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
        const bytes = crypto.getRandomValues(new Uint8Array(16));
        return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
    }

    return `${Date.now().toString(16)}${Math.random().toString(16).slice(2)}`;
};

const emitHttpError = ({ input, status, err }) => {
    if (status < 500) return;

    const traceId = err?.trace_id ?? err?.traceId;

    if (!traceId) return;

    window.dispatchEvent(
        new CustomEvent('vend:http-error', {
            detail: {
                status,
                trace_id: traceId,
                title: err?.title,
                detail: err?.detail,
                instance: err?.instance ?? input,
                code: err?.code,
            },
        }),
    );
};

/**
 * Throws the error object callers already expect for a non-OK response:
 *   - JSON / problem+json body -> { logicalException: true, err, status, globalHandled }
 *   - any other body          -> { logicalException: false, status }
 */
const throwHttpError = async (input, response) => {
    if (isJsonResponse(response)) {
        let err;

        try {
            err = await response.json();
        } catch {
            /* Declared JSON but unreadable: treat like a body-less error. */
            throw {
                logicalException: false,
                status: response.status,
            };
        }

        emitHttpError({
            input,
            status: response.status,
            err,
        });

        throw {
            logicalException: true,
            err,
            status: response.status,
            globalHandled: response.status >= 500 && !!(err?.trace_id ?? err?.traceId),
        };
    }

    throw {
        logicalException: false,
        status: response.status,
    };
};

/**
 * Authenticated portal request.
 *
 * Wrapper-only options in `init` (never forwarded to fetch):
 *   forceRefreshToken - refresh the session after the request (existing behaviour)
 *   requestId         - `true` to generate an opaque X-Request-Id, or a string to use;
 *                       the same value is reused when the request is retried after a 401
 *
 * Authentication is refreshed only for a genuine HTTP 401. Aborted requests
 * (AbortSignal) and transport failures are re-thrown unchanged: no refresh,
 * no profile request, no retry.
 */
export const fetchWithAuth = async (input, init = {}, isFormData = false) => {
    const domain = getRuntimeConfig('REACT_APP_BACKEND_URL');

    const { forceRefreshToken = false, requestId, ...fetchInit } = init ?? {};
    const signal = fetchInit.signal;

    const logout = () => {
        console.log('fetchWithAuth-logout');
        localStorage.setItem('auth', 'false');
        window.location.href = '/auth/login?force=true';
        // Zwracamy Promise który nigdy się nie rozwiązuje, aby komponent nie obsłużył błędu
        return new Promise(() => { });
    }
    const headers = {
        Accept: 'application/json',
        'Accept-Language': i18n.language || 'en',
        "X-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
    };

    if (!isFormData) {
        headers['Content-Type'] = 'application/json';
    }

    if (requestId) {
        headers[REQUEST_ID_HEADER] = typeof requestId === 'string' ? requestId : createRequestId();
    }

    /* Shared by the original request and its retry -> same X-Request-Id. */
    const withCredsInit = {
        ...fetchInit,
        credentials: 'include',
        headers: headers
    };

    /*
     * A rejected fetch is an abort or a transport failure (network error,
     * connection reset, CORS). Neither is an authentication problem, so it
     * is re-thrown as is - no refresh, no profile request, no retry.
     */
    let response = await fetch(domain + input, withCredsInit);

    // 401 zawsze musi trafić do odświeżania tokenu poniżej, niezależnie od
    // kształtu ciała odpowiedzi. Backendy Springowe odpowiadają pustym ciałem,
    // ale ForwardAuth na brzegu (Traefik -> bff-service) zwraca
    // application/problem+json - bez tego warunku gałąź JSON rzuciłaby 401
    // do komponentu i ciche odświeżenie tokenu nigdy by się nie wykonało.
    const refreshRequired = response.status === 401;

    if (!refreshRequired) {
        if (!response.ok) {
            await throwHttpError(input, response);
        }
        if (response.status === 204) {
            return null;
        }
    }

    if (refreshRequired || forceRefreshToken) {
        /* Cancelled while the 401 was in flight: the caller is gone, skip the refresh. */
        if (refreshRequired && signal?.aborted) {
            throw createAbortError();
        }

        const refreshResponse = await fetch(domain + '/portal/refresh', {
            method: 'POST',
            credentials: 'include'
        });
        //console.log('fetchWithAuth-refresh-response', refreshResponse)

        if (!refreshResponse.ok) {
            console.log('fetchWithAuth-logout because refresh response is not ok : ' + refreshResponse.status);
            const err = await refreshResponse.json();
            if (err?.error_description === "Invalid refresh token" && refreshResponse?.status === 400) {
                console.log('fetchWithAuth-logout because refresh token is invalid!');
                return logout();
            } else if (err?.error_description === "Session not active" || (refreshResponse?.status != 401 && refreshResponse?.status != 400)) {
                //user-service nie odpowiada
                console.log('fetchWithAuth-exception because session is not active or error other than 401 or 400!');
                throw { logicalException: false };
            } else {
                //"error_description": "Invalid refresh token" - refresh token is invalid (expired)
                console.log('fetchWithAuth-logout because refresh response is not ok!');
                return logout();
            }

        }
        const profile = await fetch(domain + '/portal/users/own-profile', {
            method: 'GET',
            credentials: 'include'
        });
        //console.log('fetchWithAuth-profile', profile);
        if (profile.ok) {
            localStorage.setItem('profile', JSON.stringify(await profile.json()));
        } else {
            console.log('fetchWithAuth-logout because profile response is not ok!');
            return logout();
        }
        localStorage.setItem('auth', 'true');

        // Próbujemy ponownie wykonać oryginalne żądanie (jeden raz, z tym samym X-Request-Id)
        if (refreshRequired) {
            if (signal?.aborted) {
                throw createAbortError();
            }

            response = await fetch(domain + input, withCredsInit);
        }

        if (!response.ok) {
            await throwHttpError(input, response);
        }
        if (response.status === 204) {
            return null;
        }
    }
    const contentType = response?.headers.get('content-type');
    if (contentType == 'application/json') {
        return response.json();
    } else if (contentType === 'application/octet-stream' || contentType === 'application/zip') {
        return response.blob()
    } else if (contentType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        return response.blob();
    }
    return response.body;
}
